import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { SegmentedControl, ICON_SYSTEM, ICON_LIGHT, ICON_DARK } from "./ui/SegmentedControl";
import { LiquidToggle } from "./easyui/LiquidToggle";
import { Modal } from "./ui/Modal";
import { Feedback, Panel } from "./ui/Surface";
import { UserManagement } from "./UserManagement";
import { WorkshopHolidayRow } from "./WorkshopHolidayRow";
import type { BootstrapResponse } from "../types";
import "./AccountDialog.css";

/**
 * 프로필 아래에 있던 것이 전부 이 창 하나에 들어온다.
 *
 * 한때는 아바타를 누르면 작은 창이 뜨고, 그 안의 줄을 다시 눌러 각각 다른 페이지로 갔다.
 * 고를 것이 셋뿐인데 걸음은 둘이었고, 페이지로 나가면 보던 판이 통째로 사라졌다 —
 * 계정을 고치는 일은 판을 떠날 만한 일이 아니다. 창이라 뒤에 판이 그대로 남는다.
 *
 * 한 바닥이다. 한때 탭 넷으로 갈랐는데, 탭 하나에 표 한 장씩이라 무엇이 있는지 보려면 네
 * 번을 눌러야 했고 창은 늘 아래가 비었다 — 가르는 값보다 가리는 값이 컸다. 이제 내 정보,
 * 사용자, 태블릿 인증이 차례로 서고, 감사 내역만 접혀 있다. 관리자가 아니면 첫 구역뿐이다.
 */

export function AccountDialog({ username, name, role, isAdmin, data, refresh, canEdit, theme, onThemeChange, onPasswordChanged, onClose, onLogout, onSettings, onActivityHistory, embedded = false }: {
  username: string; name: string; role: string; isAdmin: boolean;
  /** 지금 화면의 밝기. 이 기계에만 남는 값이라 저장 단추가 따로 없다. */
  theme: "system" | "light" | "dark";
  onThemeChange: (next: "system" | "light" | "dark") => void;
  /** 작업실 휴무일을 이 창에서 손본다. 판이 아직 안 떴으면 없다. */
  data?: BootstrapResponse;
  refresh?: () => Promise<void>;
  canEdit?: boolean;
  onPasswordChanged: () => void; onClose: () => void;
  onLogout: () => Promise<boolean>;
  onSettings: () => void;
  onActivityHistory?: () => void;
  /** 설정 창의 "계정" 칸 안에 들어갈 때 — 창 틀·휴무일·사용자 관리는 설정 창의 다른 칸이 맡는다. */
  embedded?: boolean;
}) {
  const [usersOpen, setUsersOpen] = useState(false);
  /* 올려 둔 프로필 사진이 있으면 보여 주기만 한다 — 올리기는 걷었다(2026-10-22 지윤 「프로필 사진 설정 필요 없을 것 같다」). */
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!embedded) return;
    let alive = true, made: string | null = null;
    void api.loadMyAvatar().then(url => { made = url; if (alive) setAvatarUrl(url); else if (url) URL.revokeObjectURL(url); }).catch(() => undefined);
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [embedded]);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setMessage(null);
    if (newPassword.length < 8) { setMessage({ tone: "error", text: "새 비밀번호를 8자 이상 입력해 주세요." }); return; }
    if (newPassword !== confirm) { setMessage({ tone: "error", text: "새 비밀번호 확인이 일치하지 않습니다." }); return; }
    setBusy(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setMessage({ tone: "success", text: "비밀번호를 변경했습니다. 새 비밀번호로 다시 로그인해 주세요." });
      setCurrentPassword(""); setNewPassword(""); setConfirm("");
      onPasswordChanged();
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "비밀번호를 변경하지 못했습니다." });
    } finally { setBusy(false); }
  };

  const body = <>
    <Panel title="내 정보">
      {/* 세 값은 읽기만 하는 것이라 한 줄로 눕힌다 — 칸을 세로로 쌓으면 고칠 수 있는
          칸처럼 보이고, 아래 비밀번호 폼이 화면 밖으로 밀린다. */}
      <dl className="account-dialog-identity">
        <div><dt>이름</dt><dd>{name}</dd></div>
        <div><dt>분류</dt><dd>{role}</dd></div>
        <div><dt>아이디</dt><dd>{username}</dd></div>
      </dl>
      <form className="account-dialog-password" onSubmit={event => void submit(event)} aria-busy={busy}>
        {message && <Feedback tone={message.tone}>{message.text}</Feedback>}
        <Field label="현재 비밀번호" required><Input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoComplete="current-password" required disabled={busy} /></Field>
        <Field label={<>새 비밀번호<span className="ui-field-unit"> 8자 이상</span></>} required><Input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} minLength={8} autoComplete="new-password" required disabled={busy} /></Field>
        <Field label="새 비밀번호 확인" required><Input type="password" value={confirm} onChange={event => setConfirm(event.target.value)} minLength={8} autoComplete="new-password" required disabled={busy} /></Field>
        {/* 무엇을 저장하는지는 옆의 칸 이름들이 말한다 — 단추가 그 말을 되풀이하면 한 줄에서
            단추가 제일 긴 물건이 된다. */}
        <Button type="submit" variant="primary" busy={busy}>저장</Button>
      </form>
    </Panel>
    {/* 밝기는 고르는 즉시 적용된다 — 저장 단추를 두면 "눌러야 바뀌나" 하고 한 번 더
        생각하게 되는데, 보고 있는 화면이 곧 그 답이라 물어볼 것이 없다. 두 쪽을 나란히
        두고 지금 쓰는 쪽을 눌린 상태로 둔다: 어느 쪽인지가 곧 설정값이다. */}
    <Panel title="화면">
      <div className="account-dialog-theme">
        {/* Claude 설정 창과 같은 세 칸 고르개다(시스템·라이트·다크). 값은
            docs/rail-reference.json 의 themeSegment 에서 온다. */}
        <span>테마</span>
        <SegmentedControl ariaLabel="테마" value={theme} onChange={onThemeChange}
          options={[{ value: "system", label: "시스템", icon: ICON_SYSTEM },
                    { value: "light", label: "라이트", icon: ICON_LIGHT },
                    { value: "dark", label: "다크", icon: ICON_DARK }]} />
      </div>
    </Panel>
    {/* 작업실이 쉬는 날. 한때 "플래너 설정"이라는 창에 있었는데, 그 이름이 틀렸다 —
        쉬는 날은 플래너만 쓰는 값이 아니라 근무 시간 계산과 정산도 함께 보는 **공장의
        사실**이다. 설정 창은 그것 하나만 남아 열고 닫는 수고가 내용보다 커져 걷었고,
        이 자리로 옮겼다: 여기는 "이 공장이 어떻게 돌아가나"를 적는 데다. */}
    {!embedded && canEdit && data && refresh && <Panel title="작업실 휴무일">
      <WorkshopHolidayRow data={data} refresh={refresh} actorName={name} />
    </Panel>}
    {!embedded && isAdmin && <UserManagement flat />}
    <footer className="account-dialog-actions">
      {/* 작업 기록 변경 이력은 위의 로그인 감사 내역과 다른 화면이다. */}
      {isAdmin && !embedded && onActivityHistory && <Button disabled={busy} onClick={onActivityHistory}>작업 기록 변경 이력</Button>}
      {isAdmin && !embedded && <Button onClick={onSettings}>설정</Button>}
      <Button disabled={busy} onClick={() => { void onLogout().then(done => { if (done) onClose(); }); }}>로그아웃</Button>
    </footer>
  </>;
  /* 설정 창의 계정 칸 = Claude 설정 Account 칸 그대로 (2026-10-19 claude.ai 실측).
     구역 제목 15/580 · 줄 = 왼쪽 이름(+회색 설명) / 오른쪽 값·칸·단추 · 줄 사이 가는 선.
     하는 일은 창일 때와 같다(내 정보 · 비밀번호 바꾸기 · 화면 · 로그아웃 · 사용자·권한). */
  if (embedded) return <div className="account-settings">
    {/* 2026-10-19 밤 지윤: 「프로필」과 「계정」을 하나로 — 줄이 넷뿐이고 로그아웃도 계정의 일이라 권한 바로 아래에 선다. */}
    <section className="account-settings-group" aria-label="계정">
      <h3>계정</h3>
      {/* 이름 줄(2026-10-22 지윤): 바꿀 수 없는 값은 아이디·권한처럼 「왼쪽 이름표 · 오른쪽 값」 — 값 자리에 동그라미(올려 둔 사진이 있으면 사진, 없으면 첫 글자)와 이름을 나란히 둔다.
          이름은 TMS 계정이 가지고 있어 여기서 못 바꾸고, 프로필 사진 올리기는 쓰지 않아 걷었다. 회색 둘째 줄은 설명 자리라 값을 넣지 않는다. */}
      <div className="account-settings-row"><div><span>이름</span></div>
        <span className="account-settings-identity">
          <span className="account-settings-avatar account-settings-avatar--small" aria-hidden="true">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : <span className="account-settings-avatar-initial">{[...name][0] ?? ""}</span>}
          </span>
          <span className="account-settings-value">{name}</span>
        </span></div>
      <div className="account-settings-row"><div><span>아이디</span></div><code className="account-settings-chip">{username}</code></div>
      <div className="account-settings-row"><div><span>권한</span></div><span className="account-settings-value">{role}</span></div>
      {/* dev/0.1.61 의 「작업 기록 변경 이력」(관리자) — 설정 창에서는 계정 칸의 한 줄로 선다. */}
      {isAdmin && onActivityHistory && <div className="account-settings-row"><div><span>작업 기록 변경 이력</span></div>
        <button type="button" className="account-settings-button" disabled={busy} onClick={onActivityHistory}>열기</button></div>}
      <div className="account-settings-row"><div><span>이 기기에서 로그아웃</span></div>
        <button type="button" className="account-settings-button" disabled={busy} onClick={() => { void onLogout().then(done => { if (done) onClose(); }); }}>로그아웃</button></div>
    </section>
    <form className="account-settings-group" onSubmit={event => void submit(event)} aria-busy={busy} aria-label="비밀번호">
      <h3>비밀번호</h3>
      {message && <Feedback tone={message.tone}>{message.text}</Feedback>}
      <label className="account-settings-row"><div><span>현재 비밀번호</span></div>
        <input className="account-settings-input" type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoComplete="current-password" required disabled={busy} /></label>
      <label className="account-settings-row"><div><span>새 비밀번호</span><p>8자 이상</p></div>
        <input className="account-settings-input" type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} minLength={8} autoComplete="new-password" required disabled={busy} /></label>
      <label className="account-settings-row"><div><span>새 비밀번호 확인</span></div>
        <input className="account-settings-input" type="password" value={confirm} onChange={event => setConfirm(event.target.value)} minLength={8} autoComplete="new-password" required disabled={busy} /></label>
      <div className="account-settings-row account-settings-row--end"><button type="submit" className="account-settings-button" disabled={busy}>{busy ? "저장 중…" : "비밀번호 저장"}</button></div>
    </form>
    {/* 2026-10-19 밤 지윤: 사용자·권한은 접어 둔다(앞으로 빠질 자리). 펼칠 때만 불러온다. */}
    {isAdmin && <details className="account-settings-group account-settings-users account-settings-fold" onToggle={event => setUsersOpen((event.currentTarget as HTMLDetailsElement).open)}>
      <summary><h3>사용자·권한</h3></summary>
      {usersOpen && <UserManagement flat />}
    </details>}
  </div>;
  return <Modal title="내 계정" className="account-dialog" busy={busy} onClose={onClose}>{body}</Modal>;
}
